import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parse, isValid } from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateFieldProps {
  id: string;
  label: string;
  /** ISO `yyyy-MM-dd`, or an empty string when unset. */
  value: string;
  onChange: (value: string) => void;
  /**
   * Dropdown caption plus a wide range suits a date of birth, where scrolling
   * back month by month from today would be absurd.
   */
  captionLayout?: 'label' | 'dropdown';
  startMonth?: Date;
  endMonth?: Date;
  /**
   * The earliest and latest day that may be picked, as ISO `yyyy-MM-dd`,
   * inclusive. A pair of fields bounding a range hand each other their value
   * so the calendar refuses a day outside it rather than the form catching it
   * after the fact. Ignored when empty or unparseable.
   */
  min?: string;
  max?: string;
}

const ISO = 'yyyy-MM-dd';

/** Parses the stored ISO string, tolerating a half-typed or empty value. */
function toDate(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, ISO, new Date());
  return isValid(parsed) ? parsed : undefined;
}

/** Calendar-backed date field for the gym member form. */
export default function DateField({
  id,
  label,
  value,
  onChange,
  captionLayout = 'label',
  startMonth,
  endMonth,
  min,
  max,
}: DateFieldProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const earliest = toDate(min ?? '');
  const latest = toDate(max ?? '');
  const disabled = [
    ...(earliest ? [{ before: earliest }] : []),
    ...(latest ? [{ after: latest }] : []),
  ];

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              className={cn(
                'w-full justify-start text-left font-normal',
                !selected && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 size-4 shrink-0" />
              {selected ? format(selected, 'dd/MM/yyyy') : t('common.pickDate')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(date) => {
                onChange(date ? format(date, ISO) : '');
                setOpen(false);
              }}
              // With nothing picked yet, open on the month the bound sits in:
              // the end of a weekend is chosen next to its start.
              defaultMonth={selected ?? earliest ?? latest ?? endMonth}
              captionLayout={captionLayout}
              startMonth={startMonth}
              endMonth={endMonth}
              disabled={disabled.length > 0 ? disabled : undefined}
              autoFocus
            />
          </PopoverContent>
        </Popover>

        {/* Both dates are optional, so clearing one has to be possible. */}
        {selected && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => onChange('')}
            aria-label={t('common.clear')}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
